import { useState } from "react";
import { CreditCard, Loader2, Star } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { trackEvent } from "@/lib/analytics";
import type { User, Session } from "@supabase/supabase-js";

interface Props {
  user: User;
  session: Session;
  isUpgrading: boolean;
  setIsUpgrading: (v: boolean) => void;
  setShowAuthModal: (v: boolean) => void;
}

const SettingsBillingSection = ({ user, session, isUpgrading, setIsUpgrading, setShowAuthModal }: Props) => {
  const handleCheckout = async (type: "starter" | "pro" | "refill") => {
    if (!user || !session) { setShowAuthModal(true); return; }
    setIsUpgrading(true);
    trackEvent("checkout_started", { plan: type });
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        headers: { Authorization: `Bearer ${session.access_token}` },
        body: { type },
      });
      if (error) throw error;
      if (data?.url) window.open(data.url, "_blank");
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error("Failed to start checkout");
    } finally {
      setIsUpgrading(false);
    }
  };

  return (
    <div className="glass-card p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 rounded-lg bg-cyan/10">
          <CreditCard className="w-5 h-5 text-cyan" />
        </div>
        <div>
          <h2 className="font-semibold text-lg">Buy Credits</h2>
          <p className="text-xs text-muted-foreground">One-time purchases. Credits never expire and are added instantly.</p>
        </div>
      </div>

      <div className="space-y-3">
        {/* Starter Pack */}
        <div className="p-4 rounded-xl bg-secondary/30 border border-border">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium">Starter Pack</h3>
              <p className="text-sm text-muted-foreground">25 credits · $9.99</p>
            </div>
            <button
              onClick={() => handleCheckout("starter")}
              disabled={isUpgrading}
              className={cn(
                "px-4 py-2 rounded-lg font-semibold text-sm",
                "bg-secondary border border-border text-foreground",
                "hover:border-cyan/50 transition-all",
                "disabled:opacity-50 disabled:pointer-events-none"
              )}
            >
              {isUpgrading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Buy — $9.99"}
            </button>
          </div>
        </div>

        {/* Pro Pack */}
        <div className="p-4 rounded-xl border-2 border-cyan/30 bg-gradient-to-r from-cyan/5 to-transparent">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-medium">Pro Pack</h3>
                <span className="px-2 py-0.5 rounded-full bg-cyan text-xs font-bold text-primary-foreground flex items-center gap-1">
                  <Star className="w-3 h-3" /> Best Value
                </span>
              </div>
              <p className="text-sm text-muted-foreground">80 credits · $24.99</p>
            </div>
            <button
              onClick={() => handleCheckout("pro")}
              disabled={isUpgrading}
              className={cn(
                "px-4 py-2 rounded-lg font-semibold text-sm",
                "bg-gradient-to-r from-cyan to-cyan-glow text-primary-foreground",
                "hover:scale-[1.02] transition-transform",
                "disabled:opacity-50 disabled:pointer-events-none"
              )}
            >
              {isUpgrading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Buy — $24.99"}
            </button>
          </div>
        </div>

        {/* 10-Credit Pack */}
        <div className="p-4 rounded-xl bg-secondary/30 border border-border">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium">10-Credit Pack</h3>
              <p className="text-sm text-muted-foreground">10 credits · $4.99</p>
            </div>
            <button
              onClick={() => handleCheckout("refill")}
              disabled={isUpgrading}
              className={cn(
                "px-4 py-2 rounded-lg font-semibold text-sm",
                "bg-secondary border border-border text-foreground",
                "hover:border-cyan/50 transition-all",
                "disabled:opacity-50 disabled:pointer-events-none"
              )}
            >
              {isUpgrading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Buy — $4.99"}
            </button>
          </div>
        </div>
      </div>

      <p className="text-[10px] text-muted-foreground/60 text-center mt-4">
        7-day refund policy · Credits never expire · Added instantly upon purchase
      </p>
    </div>
  );
};

export default SettingsBillingSection;
